// @flow
/* eslint react/no-multi-comp:0 */

/**
 * The Masonry package is a highly-customizable container for generic content.
 * Users provide data and a corresponding render method, and the layout engine
 * will control how that content is laid out onto the screen. Users can provide
 * custom layout engines and custom fetchers to control what content looks like
 * and when it gets loaded.
 *
 * The Masonry component in this file is actually split into three pieces:
 * MasonryAutoSizer, MasonryWindowScroller, and Masonry. This is because they
 * use react-virtualized, and react-virtualized use render props, but Masonry
 * needs these props in lifecycle methods.
 */

import * as React from 'react';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import classnames from 'classnames';
import WindowScroller from 'react-virtualized/dist/commonjs/WindowScroller';
import { ScrollTracker } from 'app/common/react/components/Masonry9/ScrollTracker';
import { type LayoutEngine } from 'app/common/react/components/Masonry9/LayoutEngine';
import { type renderItemProps } from 'app/common/react/components/Masonry9/Collection';
import Layout, { type Size, type Position } from 'app/common/react/components/Masonry9/Layout';
import LayoutRenderer, {
  type getVirtualOverscanProps,
  type getItemsToMeasureLimitProps,
} from 'app/common/react/components/Masonry9/LayoutRenderer';

type BaseProps<ItemType> = {|
  // Passthrough Props for LayoutRenderer:
  items: Array<ItemType>,
  renderItem: (props: renderItemProps<ItemType>) => React.Node,
  getItemLayoutOptions?: (item: ItemType) => ?Object,
  layoutEngine: LayoutEngine<ItemType>,
  onLayoutUpdated?: (layout: Layout<ItemType>) => void,
  initialLayout?: Layout<ItemType>,
  itemsToMeasureLimit?: number | ((props: getItemsToMeasureLimitProps<ItemType>) => number),
  virtualize: boolean,
  virtualOverscan?: number | ((props: getVirtualOverscanProps<ItemType>) => number),

  // Masonry Props:
  scrollContainer?: ?HTMLElement,
  scrollTracker?: ScrollTracker<ItemType>,
|};

type MasonryAutoSizerProps<ItemType> = {|
  ...BaseProps<ItemType>,
  scrollContainerSize?: Size,
  scrollPosition?: Position,
|};

type BaseState = {|
  mounted: boolean,
|};

const styles = {
  Masonry: {
    // This is a hack to prevent Chrome from scrolling down when the Collection
    // container grows in size.
    overflowAnchor: 'none',
  },
};

class MasonryAutoSizer<ItemType> extends React.Component<MasonryAutoSizerProps<ItemType>> {
  componentDidMount() {
    const { scrollTracker, scrollContainerSize, scrollPosition } = this.props;

    if (scrollTracker) {
      if (scrollContainerSize) {
        scrollTracker.handleContainerResized(scrollContainerSize);
      }

      if (scrollPosition) {
        scrollTracker.handleContainerScrolled(scrollPosition);
      }

      const layout: ?Layout<ItemType> = this.layoutRendererRef.current
        ? this.layoutRendererRef.current.getLayout()
        : null;

      if (layout) {
        scrollTracker.handleLayoutUpdated(layout);
      }
    }
  }

  layoutRendererRef: {
    current: null | LayoutRenderer<ItemType>,
  } = React.createRef();

  handleLayoutUpdated: (layout: Layout<ItemType>) => void = (layout: Layout<ItemType>) => {
    const { onLayoutUpdated, scrollTracker } = this.props;

    if (onLayoutUpdated) {
      onLayoutUpdated(layout);
    }

    if (scrollTracker) {
      scrollTracker.handleLayoutUpdated(layout);
    }
  };

  render() {
    const {
      items,
      renderItem,
      getItemLayoutOptions,
      layoutEngine,
      initialLayout,
      virtualize,
      scrollContainerSize,
      scrollPosition,
      virtualOverscan,
      itemsToMeasureLimit,
    } = this.props;

    return (
      <AutoSizer disableHeight className="AutoSizer">
        {(autoSizerSize) => {
          const contentContainerSize = {
            width: autoSizerSize.width || 0,
            height: autoSizerSize.height || 0,
          };

          return (
            <LayoutRenderer
              ref={this.layoutRendererRef}
              items={items}
              renderItem={renderItem}
              getItemLayoutOptions={getItemLayoutOptions}
              layoutEngine={layoutEngine}
              onLayoutUpdated={this.handleLayoutUpdated}
              initialLayout={initialLayout}
              scrollContainerSize={scrollContainerSize}
              scrollPosition={scrollPosition}
              contentContainerSize={contentContainerSize}
              virtualize={virtualize}
              virtualOverscan={virtualOverscan}
              itemsToMeasureLimit={itemsToMeasureLimit}
            />
          );
        }}
      </AutoSizer>
    );
  }
}

class MasonryWindowScroller<ItemType> extends React.Component<BaseProps<ItemType>> {
  windowScrollerRef: { current: null | WindowScroller } = React.createRef();

  handleWindowScrollerResized = (size: Size) => {
    const { scrollTracker } = this.props;

    if (scrollTracker) {
      scrollTracker.handleContainerResized(size);
    }
  };

  handleWindowScrollerScrolled: ({
    scrollTop: number,
    scrollLeft: number,
  }) => void = ({ scrollTop, scrollLeft }) => {
    const { scrollTracker } = this.props;

    if (scrollTracker) {
      scrollTracker.handleContainerScrolled({
        left: scrollLeft,
        top: scrollTop,
      });
    }
  };

  updatePosition() {
    if (this.windowScrollerRef.current) {
      this.windowScrollerRef.current.updatePosition();
    }
  }

  render() {
    const { scrollContainer } = this.props;

    return (
      <WindowScroller
        scrollElement={scrollContainer}
        ref={this.windowScrollerRef}
        onResize={this.handleWindowScrollerResized}
        onScroll={this.handleWindowScrollerScrolled}
      >
        {({ height, width, scrollTop, scrollLeft }) => {
          const autoSizerProps = {
            ...this.props,
            scrollContainerSize: {
              height,
              width,
            },
            scrollPosition: {
              left: scrollLeft,
              top: scrollTop,
            },
          };

          return <MasonryAutoSizer {...autoSizerProps} />;
        }}
      </WindowScroller>
    );
  }
}
export default class Masonry<ItemType> extends React.PureComponent<BaseProps<ItemType>, BaseState> {
  static defaultProps = {
    virtualize: false,
  };

  state = { mounted: false };

  componentDidMount() {
    this.setState({ mounted: true });
  }

  masonryWindowScrollerRef: {
    current: null | MasonryWindowScroller<ItemType>,
  } = React.createRef();

  updatePosition() {
    if (this.masonryWindowScrollerRef.current) {
      this.masonryWindowScrollerRef.current.updatePosition();
    }
  }

  render() {
    const { scrollContainer } = this.props;
    const { mounted } = this.state;

    return (
      <div
        className={classnames('Masonry', { 'Masonry-Premount': !mounted })}
        style={styles.Masonry}
      >
        {scrollContainer ? (
          <MasonryWindowScroller ref={this.masonryWindowScrollerRef} {...this.props} />
        ) : (
          <MasonryAutoSizer {...this.props} />
        )}
      </div>
    );
  }
}
